import { collection, doc, getDoc, getDocs, limit, orderBy, query, type Firestore, where } from 'firebase/firestore';

import { COLLECTIONS } from '@/lib/firebase/paths';
import { repairTextArtifactsInValue } from '@/lib/text-encoding';
import type { AuthorProfileDoc } from '@/types/platform';

function hydrateAuthorProfile(data: Partial<AuthorProfileDoc>, id: string): AuthorProfileDoc {
  const record = repairTextArtifactsInValue(data) as Partial<AuthorProfileDoc>;
  return { ...(record as AuthorProfileDoc), authorId: record.authorId ?? id };
}

export async function listPublicAuthors(db: Firestore, size = 24) {
  const snapshot = await getDocs(
    query(collection(db, COLLECTIONS.authorProfiles), where('isPublic', '==', true), orderBy('updatedAt', 'desc'), limit(size))
  );
  return snapshot.docs
    .map((item) => hydrateAuthorProfile(item.data() as Partial<AuthorProfileDoc>, item.id))
    .filter((item) => item.isPublic);
}

export async function getPublicAuthorBySlug(db: Firestore, slug: string) {
  const snapshot = await getDocs(query(collection(db, COLLECTIONS.authorProfiles), where('isPublic', '==', true), where('slug', '==', slug), limit(1)));
  const item = snapshot.docs[0];
  if (item) return hydrateAuthorProfile(item.data() as Partial<AuthorProfileDoc>, item.id);
  // Permanent claims retain old links after an explicit slug change. Private or
  // deleted authors are denied by rules and must not leak through an alias.
  try {
    const claimId = encodeURIComponent(slug);
    if (claimId.length > 1400) return null;
    const claim = await getDoc(doc(db, 'authorSlugClaims', claimId));
    if (!claim.exists()) return null;
    const profile = await getDoc(doc(db, COLLECTIONS.authorProfiles, claim.get('authorId')));
    return profile.exists() && profile.get('isPublic') ? hydrateAuthorProfile(profile.data(), profile.id) : null;
  } catch (error) {
    if ((error as { code?: string }).code === 'permission-denied') return null;
    throw error;
  }
}
